// FLAGS: personal-data
// The event-edit screen. Shows and changes the assigned experts (OpenUp staff) and the audience companies.
import { useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { Button } from "@openup/shared/primitives/ui/button";
import { Input } from "@openup/shared/primitives/ui/input";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import { SpacesApiError } from "@src/services/spacesError";
import FormField from "@src/components/spaces/FormField";
import CompanyPicker from "@src/components/spaces/CompanyPicker";
import MarkdownDescriptionEditor from "@src/components/spaces/MarkdownDescriptionEditor";
import EventTranslations from "@src/components/spaces/EventTranslations";
import RescheduleConfirmDialog from "@src/components/spaces/RescheduleConfirmDialog";
import SessionEditCard from "@src/components/spaces/SessionEditCard";
import {
  EditEventFormState,
  EditFormErrors,
  EditSessionFormState,
  fromEvent,
  hasEditErrors,
  isEventCancelled,
  rescheduledSessions,
  validateEditForm,
} from "@src/components/spaces/editEventForm";
import { saveEventEdits } from "@src/components/spaces/saveEventEdits";
import { formatSessionDateTime } from "@src/utils/format";
import {
  useEmployers,
  useEvent,
  useHostableExperts,
  useUpdateEventMutation,
  useUpdateSessionMutation,
} from "@src/queries/events";

const NO_ERRORS: EditFormErrors = { sessions: {} };

/** Server-rejected sessions keyed by id → message shown on the session card. */
type ServerSessionErrors = Record<string, string>;

const EventEditPage = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data: event, isLoading, isError } = useEvent(id);
  const { data: experts = [] } = useHostableExperts();
  const employersQuery = useEmployers();
  const updateEventMutation = useUpdateEventMutation(id);
  const updateSessionMutation = useUpdateSessionMutation(id);

  const [form, setForm] = useState<EditEventFormState | null>(null);
  const [errors, setErrors] = useState<EditFormErrors>(NO_ERRORS);
  const [serverErrors, setServerErrors] = useState<ServerSessionErrors>({});
  const [saving, setSaving] = useState(false);
  const [confirmingReschedule, setConfirmingReschedule] = useState(false);

  // Editability is decided once per page load; the form is initialized from
  // the loaded event and owned locally from then on.
  const initialForm = useMemo(
    () => (event ? fromEvent(event, new Date()) : null),
    [event],
  );
  const current = form ?? initialForm;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (isError || !event || !current) {
    return (
      <div className="mx-auto max-w-3xl">
        <p className="text-red-600">Event not found.</p>
        <Link
          to="/"
          className="mt-3 inline-flex items-center gap-1 text-green-700"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to events
        </Link>
      </div>
    );
  }

  // Guard against deep links: cancelled events are read-only.
  if (isEventCancelled(event)) {
    return (
      <div className="mx-auto max-w-3xl">
        <p className="text-gray-700">
          This event was cancelled and can no longer be edited.
        </p>
        <Link
          to={`/events/${event.id}`}
          className="mt-3 inline-flex items-center gap-1 text-green-700"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to event
        </Link>
      </div>
    );
  }

  const patchSession = (
    sessionId: string,
    partial: Partial<EditSessionFormState>,
  ) =>
    setForm({
      ...current,
      sessions: current.sessions.map((session) =>
        session.id === sessionId ? { ...session, ...partial } : session,
      ),
    });

  const reschedules = rescheduledSessions(current, event);

  const doSave = async (formToSave: EditEventFormState) => {
    setSaving(true);
    try {
      const outcome = await saveEventEdits(formToSave, event, {
        updateEvent: (request) => updateEventMutation.mutateAsync(request),
        updateSession: (change) => updateSessionMutation.mutateAsync(change),
      });

      if (outcome.status === "validation") {
        setErrors(outcome.errors);
        toast.error("Please fix the highlighted fields.");
      } else if (outcome.status === "no-changes") {
        toast.info("Nothing to save.");
      } else if (outcome.status === "rejected") {
        setServerErrors(outcome.rejectedSessions);
        toast.error("Some sessions could not be saved.");
      } else {
        toast.success("Changes saved.");
        navigate(`/events/${event.id}`);
      }
    } catch (error) {
      toast.error(
        error instanceof SpacesApiError
          ? error.detail.message
          : "Saving failed. Please try again.",
      );
    } finally {
      setSaving(false);
      setConfirmingReschedule(false);
    }
  };

  const onSave = async () => {
    setErrors(NO_ERRORS);
    setServerErrors({});

    // Reschedules email attendees — confirm (and re-surface the reason) first.
    // Field validation runs before the dialog so errors are never hidden under it.
    if (reschedules.length > 0) {
      const validation = validateEditForm(current);
      if (hasEditErrors(validation)) {
        setErrors(validation);
        toast.error("Please fix the highlighted fields.");
        return;
      }
      setConfirmingReschedule(true);
      return;
    }

    await doSave(current);
  };

  const onConfirmReschedule = async (reasons: Record<string, string>) => {
    const withReasons: EditEventFormState = {
      ...current,
      sessions: current.sessions.map((session) =>
        reasons[session.id] === undefined
          ? session
          : { ...session, rescheduleInfo: reasons[session.id] },
      ),
    };
    setForm(withReasons);
    await doSave(withReasons);
  };

  return (
    <div className="mx-auto max-w-4xl">
      <Link
        to={`/events/${event.id}`}
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Back to event
      </Link>

      <h1 className="mb-1 text-2xl font-semibold text-indigo-800">
        Edit event
      </h1>
      <p className="mb-6 text-sm text-gray-600">
        Changing a session&apos;s date, time, or timezone reschedules it and
        emails registered attendees. Changing experts updates the Zoom hosts
        without notifying attendees.
      </p>

      <div className="mb-6 flex flex-col gap-4">
        <FormField label="Title" required error={errors.title}>
          {({ id: fieldId, invalid }) => (
            <Input
              id={fieldId}
              value={current.title}
              aria-invalid={invalid}
              onChange={(e) => setForm({ ...current, title: e.target.value })}
            />
          )}
        </FormField>

        <FormField label="Companies" required error={errors.employerIds}>
          {({ id, invalid }) => (
            <>
              <CompanyPicker
                id={id}
                invalid={invalid}
                employers={employersQuery.data ?? []}
                selectedIds={current.employerIds}
                disabled={employersQuery.isLoading}
                onChange={(employerIds) => setForm({ ...current, employerIds })}
              />
              <p className="text-sm text-gray-600">
                Employees of any company listed here can register. Removing one
                stops new registrations from it; people already registered keep
                their place.
              </p>
            </>
          )}
        </FormField>

        <FormField
          label="Name shown on the public page"
          required={current.employerIds.length > 1}
          error={errors.employerDisplayName}
        >
          {({ id }) => (
            <>
              <Input
                id={id}
                value={current.employerDisplayName}
                placeholder={event.employerName}
                onChange={(e) =>
                  setForm({ ...current, employerDisplayName: e.target.value })
                }
              />
              <p className="text-sm text-gray-600">
                Attendees see &quot;Live session for [name] employees&quot;.
                Broaden it when the company has several entities and the event
                is for more than {event.employerName}. Leave it blank to use the
                company name.
              </p>
            </>
          )}
        </FormField>

        <FormField label="Description">
          {() => (
            <MarkdownDescriptionEditor
              value={current.description}
              onChange={(markdown) =>
                setForm({ ...current, description: markdown })
              }
            />
          )}
        </FormField>

        {event.eventType === "masterclass" && (
          <FormField label="Post-session survey link" error={errors.surveyUrl}>
            {({ id: fieldId, invalid }) => (
              <>
                <Input
                  id={fieldId}
                  type="url"
                  value={current.surveyUrl}
                  aria-invalid={invalid}
                  placeholder="https://forms.example.com/…"
                  onChange={(e) =>
                    setForm({ ...current, surveyUrl: e.target.value })
                  }
                />
                <p className="text-sm text-gray-600">
                  Attendees are sent to this third-party survey when a session
                  ends. Clearing it turns the survey off, including on the
                  already-created Zoom sessions.
                </p>
              </>
            )}
          </FormField>
        )}
      </div>

      <EventTranslations
        sourceLanguage={current.languageCode}
        title={current.title}
        description={current.description}
        sessions={event.sessions.map((session, index) => ({
          key: session.id,
          label: `Session ${index + 1}`,
          // A defaulted title is a copy of the event's, so show the event title
          // as the thing being translated rather than the copy of it.
          sourceName: session.titleIsDefaulted ? current.title : session.title,
          sourceInstructions: session.sessionInstructions ?? "",
        }))}
        translations={current.translations}
        languageError={errors.languageCode}
        onSourceLanguageChange={(languageCode) =>
          setForm({ ...current, languageCode })
        }
        onTranslationsChange={(translations) =>
          setForm({ ...current, translations })
        }
      />

      <h2 className="mb-2 text-lg font-semibold text-indigo-800">Sessions</h2>
      <div className="flex flex-col gap-4">
        {current.sessions.map((session, index) => (
          <SessionEditCard
            key={session.id}
            index={index}
            session={session}
            event={event}
            experts={experts}
            capacityError={errors.sessions[session.id]?.capacity}
            expertsError={errors.sessions[session.id]?.experts}
            serverError={serverErrors[session.id]}
            onChange={(partial) => patchSession(session.id, partial)}
          />
        ))}
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button
          variant="ghost"
          onClick={() => navigate(`/events/${event.id}`)}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <RescheduleConfirmDialog
        open={confirmingReschedule}
        pending={saving}
        sessions={reschedules.map(({ session, original, index }) => ({
          id: session.id,
          title: session.name.trim() || original.title || `Session ${index}`,
          previousTime: `${formatSessionDateTime(original.startsAt)} (${original.timeZone})`,
          newTime: `${formatSessionDateTime(`${session.date}T${session.startTime}`)} (${session.timeZone})`,
          reason: session.rescheduleInfo,
        }))}
        onConfirm={onConfirmReschedule}
        onClose={() => setConfirmingReschedule(false)}
      />
    </div>
  );
};

export default EventEditPage;
